<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <div class="bg-gray-50 p-4 overflow-auto">
        <div :class="['authentication max-w-md w-full mx-auto p-8 bg-white/95 backdrop-blur-sm border border-white/20 rounded-2xl shadow-2xl', { 'flip-horizontal': showLogin }]">
            <h1 class="text-3xl font-bold mb-4 text-center text-paletteDarkGreen">{{ t('register.title') }}</h1>
            <p class="text-lg text-center text-paletteLightGreen mb-8">{{ t('landingPage.registerBonusMessage') }}</p>
            <form @submit.prevent="registerUser" class="space-y-6" novalidate>
                <div v-if="errorMessage" class="mb-4 p-3 text-red-700 bg-red-100 border border-red-400 rounded-lg text-sm">
                    {{ errorMessage }}
                </div>
                <div>
                    <label for="email" :class="['block text-sm font-medium mb-1', validationState.email ? 'text-gray-700' : 'text-red-700']">{{ t('register.emailLabel') }}</label>
                    <input type="email" id="email" v-model="email" @input="validateOnInput('email')"
                        :class="['mt-1 block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 sm:text-sm transition duration-200',
                        validationState.email ? 'border-gray-300 focus:ring-paletteDarkGreen focus:border-paletteDarkGreen' : 'border-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500']" />
                    <p v-if="!validationState.email" class="mt-2 text-sm text-red-600">
                        {{ t('register.emailError') }}
                    </p>
                </div>
                <div>
                    <label for="username" :class="['block text-sm font-medium mb-1', validationState.username ? 'text-gray-700' : 'text-red-700']">{{ t('register.usernameLabel') }}</label>
                    <input type="text" id="username" v-model="userName" @input="validateOnInput('username')"
                        :class="['mt-1 block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 sm:text-sm transition duration-200',
                        validationState.username ? 'border-gray-300 focus:ring-paletteDarkGreen focus:border-paletteDarkGreen' : 'border-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500']" />
                    <p v-if="!validationState.username" class="mt-2 text-sm text-red-600">
                        {{ t('register.usernameError') }}
                    </p>
                </div>
                <div>
                    <label for="password" :class="['block text-sm font-medium mb-1', validationState.password ? 'text-gray-700' : 'text-red-700']">
                      {{ t('settings.password.newPassword') }}
                    </label>
                    <div class="relative">
                        <input :type="showPassword ? 'text' : 'password'" id="password" v-model="password" @input="validatePasswordInput"
                            :class="['mt-1 block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 sm:text-sm transition duration-200',
                            validationState.password ? 'border-gray-300 focus:ring-paletteDarkGreen focus:border-paletteDarkGreen' : 'border-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500']" />
                        <button type="button" @click="togglePasswordVisibility"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center mt-1">
                            <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-500 hover:text-gray-700">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-500 hover:text-gray-700">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                            </svg>
                        </button>
                    </div>
                    <p v-if="!validationState.password" class="mt-2 text-sm text-red-600">
                        {{ t('settings.password.newPasswordError') }}
                    </p>
                </div>
                <div>
                    <label for="passwordConfirmation" :class="['block text-sm font-medium mb-1', validationState.passwordConfirmation ? 'text-gray-700' : 'text-red-700']">
                      {{ t('settings.password.confirmPassword') }}
                    </label>
                    <div class="relative">
                        <input :type="showPassword ? 'text' : 'password'" id="passwordConfirmation" v-model="passwordConfirmation" @input="validatePasswordConfirmation"
                            :class="['mt-1 block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 sm:text-sm transition duration-200',
                            validationState.passwordConfirmation ? 'border-gray-300 focus:ring-paletteDarkGreen focus:border-paletteDarkGreen' : 'border-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500']" />
                    </div>
                    <p v-if="!validationState.passwordConfirmation" class="mt-2 text-sm text-red-600">
                        {{ t('settings.password.confirmPasswordError') }}
                    </p>
                </div>
                <!-- Password Requirements -->
                <div class="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 class="font-semibold">{{ t('settings.password.requirements.title') }}</h4>
                    <ul class="mt-2 text-sm space-y-1">
                        <li :class="passwordValidations.length ? 'text-paletteLightGreen' : 'text-gray-400'">
                          {{ t('settings.password.requirements.length') }}
                        </li>
                        <li :class="passwordValidations.uppercase ? 'text-paletteLightGreen' : 'text-gray-400'">
                          {{ t('settings.password.requirements.uppercase') }}
                        </li>
                        <li :class="passwordValidations.lowercase ? 'text-paletteLightGreen' : 'text-gray-400'">
                          {{ t('settings.password.requirements.lowercase') }}
                        </li>
                        <li :class="passwordValidations.special ? 'text-paletteLightGreen' : 'text-gray-400'">
                          {{ t('settings.password.requirements.special') }}
                        </li>
                    </ul>
                </div>
                <button type="submit"
                    :disabled="!isFormValid || isRegistering"
                    class="w-full py-3 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white bg-paletteDarkGreen hover:bg-paletteLightGreen focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-paletteDarkGreen transition duration-300 transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
                    <LoaderIcon v-if="isRegistering" />
                    <span v-else>{{ t('register.submitButton') }}</span>
                </button>
            </form>
            <div class="mt-8 text-center">
                <p class="text-sm text-gray-700">
                    {{ t('register.alreadyHaveAccount') }}
                    <a @click="toggleLogin" class="text-paletteDarkGreen hover:text-paletteLightGreen cursor-pointer font-medium transition duration-200">{{ t('register.loginLink') }}</a>
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth'
import { useUserStore } from '@/stores/users'
import { validatePassword, isPasswordValid } from '@/utils/passwordValidation'
import LoaderIcon from '@/components/utils/LoaderIcon.vue'

const auth = getAuth()
const email = ref('')
const userName = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const showLogin = ref(false)
const showPassword = ref(false)
const validationState = ref({
  email: true,
  username: true,
  password: true,
  passwordConfirmation: true
})
const errorMessage = ref('')

const router = useRouter()
const { t } = useI18n()
const userStore = useUserStore()
// Local loading/error state for the form itself
const isRegistering = ref(false)

const passwordValidations = reactive({
  length: false,
  uppercase: false,
  lowercase: false,
  special: false
})

const firebaseErrorMessages = {
  'auth/email-already-in-use': t('register.emailInUse'),
  'auth/invalid-email': t('register.invalidEmail'),
  'auth/operation-not-allowed': t('register.operationNotAllowed'),
  'auth/weak-password': t('register.weakPassword'),
  'auth/network-request-failed': t('register.networkError'),
  'auth/too-many-requests': t('register.tooManyAttempts'),
  'auth/internal-error': t('register.internalError')
}

const getReadableErrorMessage = (error) => {
  const errorCode = error.code || error.message
  return firebaseErrorMessages[errorCode] || t('register.defaultError')
}

const validateEmail = (emailValue) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(emailValue)
}

const validateOnInput = (field) => {
  if (field === 'email') {
    validationState.value.email = email.value && validateEmail(email.value)
  } else if (field === 'username') {
    validationState.value.username = userName.value && userName.value.length >= 3
  } else if (field === 'password') {
    validationState.value.password = password.value && password.value.length >= 6
  }
  if (errorMessage.value) {
    errorMessage.value = ''
  }
}

const validatePasswordInput = () => {
  Object.assign(passwordValidations, validatePassword(password.value))
  validationState.value.password = isPasswordValid(passwordValidations)
  validatePasswordConfirmation()
}

const validatePasswordConfirmation = () => {
  validationState.value.passwordConfirmation =
    passwordConfirmation.value &&
    passwordConfirmation.value === password.value
}

const isFormValid = computed(() => {
  return validationState.value.email &&
    validationState.value.username &&
    validationState.value.password &&
    validationState.value.passwordConfirmation
})

const registerUser = async () => {
  if (!email.value || !password.value || !userName.value || !passwordConfirmation.value) {
    errorMessage.value = t('register.fillAllFields')
    return
  }

  if (password.value !== passwordConfirmation.value) {
    errorMessage.value = t('register.passwordsDoNotMatch')
    return
  }

  isRegistering.value = true
  errorMessage.value = null

  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email.value, password.value)
    const firebaseUser = userCredential.user
    const idToken = await firebaseUser.getIdToken()

    await userStore.registerUserInBackend(firebaseUser, idToken, {
      userName: userName.value,
      locale: navigator.language || 'en'
    })

    router.push({ name: 'Dashboard' })
  } catch (error) {
    console.error('Registration failed:', error)
    errorMessage.value = getReadableErrorMessage(error)
  } finally {
    isRegistering.value = false
  }
}

const toggleLogin = () => {
  showLogin.value = !showLogin.value
  setTimeout(() => {
    router.push('/login')
  }, 500)
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}
</script>

<style scoped>
.authentication {
    transition: transform 0.6s;
    transform-style: preserve-3d;
}

.flip-horizontal {
    transform: rotateY(180deg);
}

.bg-gradient-to-br {
    background-size: 200% 200%;
    animation: gradient 15s ease infinite;
}

@keyframes gradient {
    0% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }
}
</style>
